import React, { useState } from 'react';
import GaugeChart from './GaugeChart';

/**
 * دالة مساعدة لحساب كلاس اللون بناءً على القيمة وتكوين السنسور
 */
const getColorClass = (value, colorConfig) => {
    if (value === undefined || value === null || isNaN(value)) {
        return 'text-slate-400'; // لون رمادي في حالة عدم وجود داتا
    }

    const v = Number(value);

    // إذا لم يتم توفير إعدادات الألوان، استخدم لون افتراضي (مثلاً الأخضر الجيو-نوتريا)
    if (!colorConfig || !colorConfig.logic || !colorConfig.ranges) {
        return 'text-[#C47A2C]';
    }

    const { logic, ranges, colors } = colorConfig;

    // المنطق 1: الأحمر منخفض، الأخضر مرتفع (مثال: الرطوبة)
    if (logic === 'linearLowToHigh') {
        if (v <= ranges.red) return colors.red;
        if (v <= ranges.yellow) return colors.yellow;
        return colors.green;
    }

    // المنطق 2: الأخضر منخفض، الأحمر مرتفع (مثال: الملوحة)
    if (logic === 'linearHighToLow') {
        if (v <= ranges.green) return colors.green;
        if (v <= ranges.yellow) return colors.yellow;
        return colors.red;
    }

    // المنطق 3: منحنى جرس، الأخضر في المنتصف والأحمر والأصفر على الأطراف (مثال: pH)
    if (logic === 'bellCurve') {
        if (v <= ranges.redLow) return colors.red; // حامضي جداً
        if (v <= ranges.yellowLow) return colors.yellow; // حامضي خفيف
        if (v <= ranges.green) return colors.green; // مثالي
        if (v <= ranges.yellowHigh) return colors.yellow; // قلوي خفيف
        return colors.red; // قلوي جداً
    }

    return 'text-[#C47A2C]'; // افتراضي
};

/**
 * تحويل كلاس اللون إلى كود Hex لاستخدامه في التوهج والحدود (inline styles)
 */
const ACCENT_HEX = {
    'text-emerald-500': '#10b981',
    'text-yellow-500': '#eab308',
    'text-red-500': '#ef4444',
    'text-slate-400': '#94a3b8',
    'text-[#C47A2C]': '#C47A2C',
};

/** مقدار الزيادة/النقصان لكل سنسور عند الضغط على + / - */
const STEP_BY_KEY = {
    Soil_pH: 0.1,
    Electrochemical_Signal: 10,
    Nitrogen_Level: 5,
    Phosphorus_Level: 5,
    Potassium_Level: 5,
    Salinity: 10,
    TDS: 10,
};

/** السنسورات اللي ممكن تاخد قيم بالسالب (درجات الحرارة) */
const ALLOWS_NEGATIVE = new Set(['Ambient_Temperature', 'Soil_Temperature']);

const SensorCard = ({ dataMode, sensor, manualInput, displayVal, isLive, disconnectedMsg, onManualChange, theme }) => {
    const isSoilM = sensor.key === 'Soil_Moisture';
    const IconComponent = sensor.icon;
    const [isFocused, setIsFocused] = useState(false);

    // حساب اللون ديناميكياً بناءً على القيمة وتكوين السنسور
    const valueColorClass = getColorClass(displayVal, sensor.colorConfig);

    // ── إعدادات الإدخال اليدوي ──
    const accent = ACCENT_HEX[valueColorClass] || '#C47A2C';
    const step = STEP_BY_KEY[sensor.key] ?? 1;
    // الجيدج بياخد رقم دايماً، حتى لو الخانة فاضية أثناء الكتابة
    const numericVal = manualInput === '' || manualInput === null || manualInput === undefined || isNaN(Number(manualInput))
        ? 0
        : Number(manualInput);

    /** زيادة/نقصان القيمة مع تقريب يمنع أخطاء الفاصلة العائمة (0.1 + 0.2) */
    const bump = (delta) => {
        const next = Number((numericVal + delta).toFixed(2));
        onManualChange(String(ALLOWS_NEGATIVE.has(sensor.key) ? next : Math.max(0, next)));
    };

    // حساب تأثير التوهج للعنوان
    const glowClass = valueColorClass.includes('emerald')
        ? 'shadow-[0_0_20px_rgba(16,185,129,0.25)] border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10'
        : valueColorClass.includes('yellow')
            ? 'shadow-[0_0_20px_rgba(234,179,8,0.25)] border-yellow-500/20 bg-yellow-500/5 dark:bg-yellow-500/10'
            : valueColorClass.includes('red')
                ? 'shadow-[0_0_20px_rgba(239,68,68,0.25)] border-red-500/20 bg-red-500/5 dark:bg-red-500/10'
                : 'shadow-[0_0_20px_rgba(196,122,44,0.25)] border-[#C47A2C]/20 bg-[#C47A2C]/5 dark:bg-[#C47A2C]/10';

    return (
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-md border border-[#1B263B]/5 dark:border-slate-700 p-6 xl:p-8 flex flex-col h-[340px] xl:h-[380px] transition-colors group relative overflow-hidden">
            {/* الانديكوتر الصغير في الزاوية */}
            <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${valueColorClass.replace('text-', 'bg-')}`}></div>

            <div className="flex justify-center items-center mb-4 relative z-10">
                <div className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-full backdrop-blur-md border transition-all ${glowClass} max-w-full min-w-0`}>
                    {IconComponent && (
                        // في الإدخال اليدوي بنلف الأيقونة في span بتوهج نابض،
                        // عشان ما نلغيش الأنيميشن الأصلي بتاع الأيقونة نفسها
                        <span className={`shrink-0 flex ${valueColorClass} ${dataMode === 'manual' ? 'animate-breathe-glow' : ''}`}>
                            <IconComponent className={`w-5 h-5 transition-transform group-hover:scale-110 ${valueColorClass} ${sensor.iconAnimation || ''}`} strokeWidth={2.5} />
                        </span>
                    )}
                    <h4 className={`text-base font-bold tracking-wide ${valueColorClass} truncate`}>{sensor.label}</h4>
                </div>
            </div>

            <div className="flex flex-col items-center flex-grow w-full justify-center relative z-10 min-h-0">
                {dataMode === 'manual' ? (
                    <div className="flex flex-col items-center justify-between w-full h-full min-h-0 gap-2">
                        {/* نفس الجيدج بتاع الـ Live بس متحرك بالقيمة المدخلة يدوياً */}
                        <div className="w-full flex-1 min-h-0 flex items-center justify-center px-2">
                            <div className="w-full h-full max-w-[230px] sm:max-w-[250px] xl:max-w-[280px] flex items-center justify-center">
                                <GaugeChart
                                    value={numericVal}
                                    unit={sensor.unit}
                                    colorConfig={sensor.colorConfig}
                                    theme={theme || 'light'}
                                />
                            </div>
                        </div>

                        {/* صندوق إدخال رقمي متوهج بلون حالة القراءة */}
                        <div
                            className="w-full max-w-[230px] shrink-0 flex items-stretch rounded-2xl border-2 overflow-hidden backdrop-blur-md bg-white/60 dark:bg-slate-900/60 transition-all duration-300"
                            style={{
                                borderColor: isFocused ? accent : `${accent}66`,
                                boxShadow: isFocused
                                    ? `0 0 0 3px ${accent}33, 0 0 22px ${accent}66`
                                    : `0 0 14px ${accent}26`
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => bump(-step)}
                                aria-label={`decrease ${sensor.label}`}
                                className="px-3.5 text-xl font-bold leading-none select-none transition-colors hover:bg-black/5 dark:hover:bg-white/10 active:scale-90"
                                style={{ color: accent }}
                            >
                                &#8722;
                            </button>

                            <input
                                type="number"
                                step={step}
                                value={manualInput}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => { setIsFocused(false); if (manualInput === '') onManualChange('0'); }}
                                onChange={(e) => onManualChange(e.target.value)}
                                className="flex-1 min-w-0 bg-transparent text-center font-extrabold text-lg py-2.5 outline-none"
                                style={{ color: accent }}
                            />

                            <button
                                type="button"
                                onClick={() => bump(step)}
                                aria-label={`increase ${sensor.label}`}
                                className="px-3.5 text-xl font-bold leading-none select-none transition-colors hover:bg-black/5 dark:hover:bg-white/10 active:scale-90"
                                style={{ color: accent }}
                            >
                                +
                            </button>
                        </div>

                        {sensor.unit && (
                            <div className="text-[11px] font-semibold tracking-wide text-gray-400 dark:text-gray-500 shrink-0">
                                {sensor.unit}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="relative flex flex-col items-center justify-center w-full h-full flex-grow px-2 min-h-0">
                        <div className="w-full h-full max-w-[250px] sm:max-w-[280px] xl:max-w-[320px] flex items-center justify-center">
                            <GaugeChart
                                value={displayVal}
                                unit={sensor.unit}
                                colorConfig={sensor.colorConfig}
                                theme={theme || 'light'}
                            />
                        </div>
                        {isSoilM && isLive && (
                            <div className="absolute top-1 ltr:right-1 rtl:left-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 border border-white dark:border-slate-800"></span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SensorCard;